import { Position } from '../types/game';

export type GemColor = 'red' | 'blue' | 'green' | 'yellow' | 'purple' | 'orange';
export type StarColor = 'red' | 'blue' | 'yellow' | 'green' | 'purple' | 'pink';
export type AnimalColor = 'red' | 'blue' | 'green' | 'yellow' | 'purple' | 'orange';
export type FruitType = 'apple' | 'banana' | 'grape' | 'orange' | 'strawberry' | 'watermelon';

export type Match3Mode = 'classic' | 'stars' | 'happy' | 'fruit';

export interface Gem {
  color: GemColor;
  id: string;
  type: 'gem';
}

export interface Star {
  color: StarColor;
  id: string;
  type: 'star';
}

export interface Animal {
  color: AnimalColor;
  id: string;
  type: 'animal';
}

export interface Fruit {
  type: FruitType;
  id: string;
  kind: 'fruit';
}

export type Match3Piece = Gem | Star | Animal | Fruit;

export interface Match3GameState {
  board: (Match3Piece | null)[][];
  score: number;
  level: number;
  moves: number;
  targetScore: number;
  isAnimating: boolean;
  selectedGem: Position | null;
  matchedGems: Position[];
  lastCombo: number;
  mode: Match3Mode;
  baseTargetScore: number; // 基础目标分数，用于星星模式
}

// 临时兼容性类型 - 保持向后兼容
export type GemCompat = Gem | Star;
export type BoardCompat = (GemCompat | null)[][];

// 创建随机宝石
export const createRandomGem = (): Gem => {
  const colors: GemColor[] = ['red', 'blue', 'green', 'yellow', 'purple', 'orange'];
  return {
    color: colors[Math.floor(Math.random() * colors.length)],
    id: Math.random().toString(36).substr(2, 9),
    type: 'gem'
  };
};

// 创建随机星星
export const createRandomStar = (): Star => {
  const colors: StarColor[] = ['red', 'blue', 'yellow', 'green', 'purple', 'pink'];
  return {
    color: colors[Math.floor(Math.random() * colors.length)],
    id: Math.random().toString(36).substr(2, 9),
    type: 'star'
  };
};

// 创建随机小动物
export const createRandomAnimal = (): Animal => {
  const colors: AnimalColor[] = ['red', 'blue', 'green', 'yellow', 'purple', 'orange'];
  return {
    color: colors[Math.floor(Math.random() * colors.length)],
    id: Math.random().toString(36).substr(2, 9),
    type: 'animal'
  };
};

// 创建随机水果
export const createRandomFruit = (): Fruit => {
  const fruits: FruitType[] = ['apple', 'banana', 'grape', 'orange', 'strawberry', 'watermelon'];
  return {
    type: fruits[Math.floor(Math.random() * fruits.length)],
    id: Math.random().toString(36).substr(2, 9),
    kind: 'fruit'
  };
};

// 创建初始游戏板
export const createInitialBoard = (size: number = 10, mode: Match3Mode = 'classic'): (Match3Piece | null)[][] => {
  const board: (Match3Piece | null)[][] = [];
  for (let row = 0; row < size; row++) {
    board[row] = [];
    for (let col = 0; col < size; col++) {
      let piece: Match3Piece;

      if (mode === 'classic') {
        piece = createRandomGem();
      } else if (mode === 'stars') {
        piece = createRandomStar();
      } else if (mode === 'happy') {
        piece = createRandomAnimal();
      } else {
        piece = createRandomFruit();
      }

      // 确保初始板面没有3连
      while (wouldCreateMatch(board, row, col, piece, size)) {
        piece = mode === 'classic' ? createRandomGem() :
               mode === 'stars' ? createRandomStar() :
               mode === 'happy' ? createRandomAnimal() :
               createRandomFruit();
      }
      board[row][col] = piece;
    }
  }
  return board;
};

// 检查是否会形成匹配
const wouldCreateMatch = (board: (Match3Piece | null)[][], row: number, col: number, piece: Match3Piece, size: number): boolean => {
  // 获取棋子的标识符（颜色或类型）
  const pieceIdentifier = (piece as Gem | Star | Animal).color || (piece as Fruit).type;

  // 检查水平方向
  let horizontalCount = 1;

  // 向左检查
  for (let c = col - 1; c >= 0 && board[row] && board[row][c] && getPieceIdentifier(board[row][c]!) === pieceIdentifier; c--) {
    horizontalCount++;
  }

  // 向右检查
  for (let c = col + 1; c < size && board[row] && board[row][c] && getPieceIdentifier(board[row][c]!) === pieceIdentifier; c++) {
    horizontalCount++;
  }

  if (horizontalCount >= 3) return true;

  // 检查垂直方向
  let verticalCount = 1;

  // 向上检查
  for (let r = row - 1; r >= 0 && board[r] && board[r][col] && getPieceIdentifier(board[r][col]!) === pieceIdentifier; r--) {
    verticalCount++;
  }

  // 向下检查
  for (let r = row + 1; r < size && board[r] && board[r][col] && getPieceIdentifier(board[r][col]!) === pieceIdentifier; r++) {
    verticalCount++;
  }

  return verticalCount >= 3;
};

// 获取棋子的标识符（颜色或类型）
const getPieceIdentifier = (piece: Match3Piece): string => {
  if ((piece as Gem | Star | Animal).color) {
    return (piece as Gem | Star | Animal).color;
  }
  return (piece as Fruit).type;
};

// 查找所有匹配
export const findAllMatches = (board: (Match3Piece | null)[][]): Position[] => {
  const size = board.length;
  const matched: Position[] = [];
  const visited = new Set<string>();

  for (let row = 0; row < size; row++) {
    for (let col = 0; col < size; col++) {
      const piece = board[row][col];
      if (!piece) continue;

      const key = `${row},${col}`;
      if (visited.has(key)) continue;

      // 检查水平匹配
      const horizontalMatches = findHorizontalMatches(board, row, col);
      if (horizontalMatches.length >= 3) {
        horizontalMatches.forEach(pos => {
          const posKey = `${pos.row},${pos.col}`;
          if (!visited.has(posKey)) {
            matched.push(pos);
            visited.add(posKey);
          }
        });
      }

      // 检查垂直匹配
      const verticalMatches = findVerticalMatches(board, row, col);
      if (verticalMatches.length >= 3) {
        verticalMatches.forEach(pos => {
          const posKey = `${pos.row},${pos.col}`;
          if (!visited.has(posKey)) {
            matched.push(pos);
            visited.add(posKey);
          }
        });
      }
    }
  }

  return matched;
};

// 查找水平匹配
const findHorizontalMatches = (board: (Match3Piece | null)[][], row: number, col: number): Position[] => {
  const size = board.length;
  const piece = board[row][col];
  if (!piece) return [];

  const matches: Position[] = [{ row, col }];
  const identifier = getPieceIdentifier(piece);

  // 向左查找
  for (let c = col - 1; c >= 0 && board[row][c] && getPieceIdentifier(board[row][c]!) === identifier; c--) {
    matches.unshift({ row, col: c });
  }

  // 向右查找
  for (let c = col + 1; c < size && board[row][c] && getPieceIdentifier(board[row][c]!) === identifier; c++) {
    matches.push({ row, col: c });
  }

  return matches;
};

// 查找垂直匹配
const findVerticalMatches = (board: (Match3Piece | null)[][], row: number, col: number): Position[] => {
  const size = board.length;
  const piece = board[row][col];
  if (!piece) return [];

  const matches: Position[] = [{ row, col }];
  const identifier = getPieceIdentifier(piece);

  // 向上查找
  for (let r = row - 1; r >= 0 && board[r][col] && getPieceIdentifier(board[r][col]!) === identifier; r--) {
    matches.unshift({ row: r, col });
  }

  // 向下查找
  for (let r = row + 1; r < size && board[r][col] && getPieceIdentifier(board[r][col]!) === identifier; r++) {
    matches.push({ row: r, col });
  }

  return matches;
};

// 消除匹配的宝石
export const removeGems = (board: (Match3Piece | null)[][], positions: Position[]): (Match3Piece | null)[][] => {
  const newBoard = board.map(row => [...row]);
  positions.forEach(pos => {
    newBoard[pos.row][pos.col] = null;
  });
  return newBoard;
};

// 让宝石下落
export const dropGems = (board: (Match3Piece | null)[][]): (Match3Piece | null)[][] => {
  const size = board.length;
  const newBoard = board.map(row => [...row]);

  for (let col = 0; col < size; col++) {
    let writePos = size - 1;

    // 从下往上扫描，将非空宝石下移
    for (let row = size - 1; row >= 0; row--) {
      if (newBoard[row][col] !== null) {
        if (row !== writePos) {
          newBoard[writePos][col] = newBoard[row][col];
          newBoard[row][col] = null;
        }
        writePos--;
      }
    }
  }

  return newBoard;
};

// 星星模式下的向左移动（当整列为空时）
export const shiftGemsLeft = (board: (Match3Piece | null)[][]): (Match3Piece | null)[][] => {
  const size = board.length;
  const newBoard = board.map(row => [...row]);

  let writeCol = 0;

  // 从左到右处理每一列
  for (let col = 0; col < size; col++) {
    // 检查当前列是否为空
    const isColumnEmpty = newBoard.every(row => row[col] === null);

    if (!isColumnEmpty) {
      // 如果当前列不为空，且需要移动到左边
      if (col !== writeCol) {
        // 将整列移动到writeCol位置
        for (let row = 0; row < size; row++) {
          newBoard[row][writeCol] = newBoard[row][col];
          newBoard[row][col] = null;
        }
      }
      writeCol++;
    }
  }

  return newBoard;
};

// 填充空位
export const fillEmptySpaces = (board: (Match3Piece | null)[][], mode: Match3Mode = 'classic'): (Match3Piece | null)[][] => {
  const size = board.length;
  const newBoard = board.map(row => [...row]);

  // 星星消除模式下不填充空位，直到清屏
  if (mode === 'stars') {
    return newBoard;
  }

  // 根据模式填充新的棋子
  for (let col = 0; col < size; col++) {
    for (let row = 0; row < size; row++) {
      if (newBoard[row][col] === null) {
        if (mode === 'classic') {
          newBoard[row][col] = createRandomGem();
        } else if (mode === 'happy') {
          newBoard[row][col] = createRandomAnimal();
        } else if (mode === 'fruit') {
          newBoard[row][col] = createRandomFruit();
        }
      }
    }
  }

  return newBoard;
};

// 计算得分
export const calculateScore = (matchedCount: number, combo: number): number => {
  const baseScore = matchedCount * 10;
  const comboBonus = combo * 5;
  const sizeBonus = matchedCount > 3 ? (matchedCount - 3) * 15 : 0;
  return baseScore + comboBonus + sizeBonus;
};

// 检查是否还有可能的移动（支持不同模式）
export const hasValidMoves = (board: (Match3Piece | null)[][], mode: Match3Mode = 'classic'): boolean => {
  const size = board.length;
  const minRequired = mode === 'stars' ? 2 : 3;

  // 检查是否已经有匹配
  const matches = findAllMatches(board);
  if (matches.length > 0) {
    return true;
  }

  // 检查是否还有可能的连接
  for (let row = 0; row < size; row++) {
    for (let col = 0; col < size; col++) {
      if (board[row][col] !== null) {
        // 检查是否有可能的连接（根据模式）
        const piece = board[row][col]!;
        const identifier = getPieceIdentifier(piece);

        // 检查水平方向
        let horizontalCount = 1;
        for (let c = col - 1; c >= 0 && board[row][c] && getPieceIdentifier(board[row][c]!) === identifier; c--) {
          horizontalCount++;
        }
        for (let c = col + 1; c < size && board[row][c] && getPieceIdentifier(board[row][c]!) === identifier; c++) {
          horizontalCount++;
        }

        if (horizontalCount >= minRequired) return true;

        // 检查垂直方向
        let verticalCount = 1;
        for (let r = row - 1; r >= 0 && board[r][col] && getPieceIdentifier(board[r][col]!) === identifier; r--) {
          verticalCount++;
        }
        for (let r = row + 1; r < size && board[r][col] && getPieceIdentifier(board[r][col]!) === identifier; r++) {
          verticalCount++;
        }

        if (verticalCount >= minRequired) return true;
      }
    }
  }

  // 如果没有匹配，检查是否还有棋子
  for (let row = 0; row < size; row++) {
    for (let col = 0; col < size; col++) {
      if (board[row][col] !== null) {
        return true; // 还有棋子，可以继续游戏
      }
    }
  }

  // 棋盘为空，游戏结束
  return false;
};

// 检查是否有可消除的组合（专门用于星星模式提示）
export const hasAvailableMoves = (board: (Match3Piece | null)[][], mode: Match3Mode = 'classic'): boolean => {
  const size = board.length;
  const minRequired = mode === 'stars' ? 2 : 3;

  for (let row = 0; row < size; row++) {
    for (let col = 0; col < size; col++) {
      const piece = board[row][col];
      if (!piece) continue;

      // 检查水平方向
      let horizontalCount = 1;
      const identifier = getPieceIdentifier(piece);
      for (let c = col - 1; c >= 0 && board[row][c] && getPieceIdentifier(board[row][c]!) === identifier; c--) {
        horizontalCount++;
      }
      for (let c = col + 1; c < size && board[row][c] && getPieceIdentifier(board[row][c]!) === identifier; c++) {
        horizontalCount++;
      }

      if (horizontalCount >= minRequired) return true;

      // 检查垂直方向
      let verticalCount = 1;
      for (let r = row - 1; r >= 0 && board[r][col] && getPieceIdentifier(board[r][col]!) === identifier; r--) {
        verticalCount++;
      }
      for (let r = row + 1; r < size && board[r][col] && getPieceIdentifier(board[r][col]!) === identifier; r++) {
        verticalCount++;
      }

      if (verticalCount >= minRequired) return true;
    }
  }

  return false;
};

// 开心消消乐模式：查找三个同色宝石连成直线
export const findHappyMatches = (board: (Match3Piece | null)[][]): Position[] => {
  const size = board.length;
  const matched: Position[] = [];

  // 检查水平方向的三个连珠
  for (let row = 0; row < size; row++) {
    for (let col = 0; col <= size - 3; col++) {
      const piece1 = board[row][col];
      const piece2 = board[row][col + 1];
      const piece3 = board[row][col + 2];

      if (piece1 && piece2 && piece3 &&
          getPieceIdentifier(piece1) === getPieceIdentifier(piece2) &&
          getPieceIdentifier(piece2) === getPieceIdentifier(piece3)) {
        matched.push(
          { row, col },
          { row, col: col + 1 },
          { row, col: col + 2 }
        );
      }
    }
  }

  // 检查垂直方向的三个连珠
  for (let col = 0; col < size; col++) {
    for (let row = 0; row <= size - 3; row++) {
      const piece1 = board[row][col];
      const piece2 = board[row + 1][col];
      const piece3 = board[row + 2][col];

      if (piece1 && piece2 && piece3 &&
          getPieceIdentifier(piece1) === getPieceIdentifier(piece2) &&
          getPieceIdentifier(piece2) === getPieceIdentifier(piece3)) {
        matched.push(
          { row, col },
          { row: row + 1, col },
          { row: row + 2, col }
        );
      }
    }
  }

  // 去重
  const uniqueMatches = Array.from(
    new Map(matched.map(pos => [`${pos.row},${pos.col}`, pos])).values()
  );

  return uniqueMatches;
};

// 开心消消乐模式：检查是否有可用的三连珠
export const hasHappyMoves = (board: (Match3Piece | null)[][]): boolean => {
  // 首先检查是否已经有现成的三连珠
  const existingMatches = findHappyMatches(board);
  if (existingMatches.length > 0) {
    return true;
  }

  // 如果没有现成的三连珠，检查是否有可能通过交换形成三连珠
  return canCreateHappyMatch(board);
};

// 检查是否有可能通过交换形成三连珠
export const canCreateHappyMatch = (board: (Match3Piece | null)[][]): boolean => {
  const size = board.length;

  // 遍历每个位置，尝试与相邻位置交换
  for (let row = 0; row < size; row++) {
    for (let col = 0; col < size; col++) {
      const currentPiece = board[row][col];
      if (!currentPiece) continue;

      // 尝试与右边交换
      if (col < size - 1) {
        const rightPiece = board[row][col + 1];
        if (rightPiece && getPieceIdentifier(currentPiece) !== getPieceIdentifier(rightPiece)) {
          // 模拟交换
          const testBoard = board.map(r => [...r]);
          testBoard[row][col] = rightPiece;
          testBoard[row][col + 1] = currentPiece;

          // 检查是否能形成三连珠
          if (findHappyMatches(testBoard).length > 0) {
            return true;
          }
        }
      }

      // 尝试与下边交换
      if (row < size - 1) {
        const bottomPiece = board[row + 1][col];
        if (bottomPiece && getPieceIdentifier(currentPiece) !== getPieceIdentifier(bottomPiece)) {
          // 模拟交换
          const testBoard = board.map(r => [...r]);
          testBoard[row][col] = bottomPiece;
          testBoard[row + 1][col] = currentPiece;

          // 检查是否能形成三连珠
          if (findHappyMatches(testBoard).length > 0) {
            return true;
          }
        }
      }
    }
  }

  return false;
};

// 获取目标分数
export const getTargetScore = (level: number): number => {
  // 提高难度：第1关1000分，之后每关增加1000分
  return 1000 + (level - 1) * 1000;
};